import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Mock step content ─────────────────────────────────────────────────────

const STEPS = [
  {
    label: 'ASSESS',
    title: 'Self-Assessment',
    subtitle: 'What kind of trader are you?',
    cards: ['New Trader', 'Backtesting', 'Proven'],
    icon: '🛡',
  },
  {
    label: 'STRATEGY',
    title: 'Choose Your Weapon',
    subtitle: 'Which model fits your edge?',
    cards: ['TCL Model', 'SMOG Model'],
    icon: '⚔',
  },
  {
    label: 'FIRM',
    title: 'Select Your Arena',
    subtitle: 'Pick your prop firm and account.',
    cards: ['Apex', 'Topstep', 'Tradeify', 'MFF'],
    icon: '🏰',
  },
  {
    label: 'PROTOCOL',
    title: 'Your Prescription',
    subtitle: 'Armed and ready.',
    cards: [],
    isPrescription: true,
    icon: '📜',
  },
];

// ─── Shield SVG icon ────────────────────────────────────────────────────────

function ShieldIcon({
  size = 24,
  filled = false,
  glowColor = 'rgba(232, 148, 58, 0)',
  className = '',
}: {
  size?: number;
  filled?: boolean;
  glowColor?: string;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      style={{ filter: glowColor !== 'rgba(232, 148, 58, 0)' ? `drop-shadow(0 0 8px ${glowColor})` : undefined }}
    >
      <path
        d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z"
        fill={filled ? 'rgba(232, 148, 58, 0.2)' : 'none'}
        stroke={filled ? '#E8943A' : 'rgba(255,255,255,0.25)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {filled && (
        <path
          d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z"
          fill="rgba(232, 148, 58, 0.08)"
          stroke="none"
        />
      )}
    </svg>
  );
}

// ─── Ember particles ────────────────────────────────────────────────────────

function EmberBurst({ count, intensity }: { count: number; intensity: number }) {
  const embers = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    const distance = 60 + Math.random() * 80 * intensity;
    const size = 2 + Math.random() * 4;
    const duration = 0.6 + Math.random() * 0.4;

    return {
      id: i,
      targetX: Math.cos(angle) * distance,
      targetY: Math.sin(angle) * distance,
      size,
      duration,
      delay: Math.random() * 0.15,
      color: Math.random() > 0.3
        ? `rgba(232, 148, 58, ${0.6 + Math.random() * 0.4})`
        : `rgba(255, 200, 100, ${0.5 + Math.random() * 0.5})`,
    };
  });

  return (
    <>
      {embers.map((ember) => (
        <motion.div
          key={ember.id}
          className="absolute rounded-full"
          style={{
            width: ember.size,
            height: ember.size,
            backgroundColor: ember.color,
            left: '50%',
            top: '50%',
            marginLeft: -ember.size / 2,
            marginTop: -ember.size / 2,
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{
            x: ember.targetX,
            y: ember.targetY,
            scale: 0,
            opacity: 0,
          }}
          transition={{
            duration: ember.duration,
            delay: ember.delay,
            ease: 'easeOut',
          }}
        />
      ))}
    </>
  );
}

// ─── The Forge Overlay (expanded progress tracker) ──────────────────────────

function ForgeOverlay({
  stepIndex,
  onComplete,
}: {
  stepIndex: number;
  onComplete: () => void;
}) {
  const emberCount = 4 + stepIndex * 3; // More embers each step
  const isPrescription = stepIndex === STEPS.length - 1;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Dark backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.95 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      />

      {/* Radial pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.5, 0] }}
        transition={{ duration: 0.8, times: [0, 0.4, 1] }}
      >
        <div
          className="w-full h-full"
          style={{
            background: 'radial-gradient(circle at center, rgba(232, 148, 58, 0.15) 0%, transparent 50%)',
          }}
        />
      </motion.div>

      {/* Central shield */}
      <div className="relative z-10">
        {/* Ember particles */}
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <EmberBurst count={emberCount} intensity={1 + stepIndex * 0.3} />
        </motion.div>

        {/* Shield icon animating through temperature */}
        <motion.div
          className="relative"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: 'spring' as const,
            stiffness: 300,
            damping: 20,
            delay: 0.05,
          }}
          onAnimationComplete={() => {
            // Hold for a beat then complete
            setTimeout(onComplete, isPrescription ? 600 : 400);
          }}
        >
          {/* Glow ring */}
          <motion.div
            className="absolute -inset-8 rounded-full"
            animate={{
              boxShadow: [
                '0 0 20px rgba(232, 148, 58, 0.1)',
                '0 0 60px rgba(232, 148, 58, 0.3)',
                '0 0 30px rgba(232, 148, 58, 0.15)',
              ],
            }}
            transition={{ duration: 0.6, times: [0, 0.5, 1] }}
          />

          {/* The shield itself — color temperature rises */}
          <motion.div
            animate={{
              filter: [
                'brightness(0.5) saturate(0.3)',
                'brightness(1.5) saturate(1.2)',
                'brightness(1.0) saturate(1.0)',
              ],
            }}
            transition={{ duration: 0.6, times: [0, 0.6, 1] }}
          >
            <ShieldIcon
              size={80}
              filled
              glowColor="rgba(232, 148, 58, 0.6)"
            />
          </motion.div>

          {/* Step label below shield */}
          <motion.p
            className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-[11px] font-mono text-[#E8943A]/70 tracking-[0.2em] uppercase whitespace-nowrap"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {STEPS[stepIndex].label}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Prescription Forge: shields converge, shatter, fragments fly ───────────

function PrescriptionForge({ onComplete }: { onComplete: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {/* Dark backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.97 }}
        exit={{ opacity: 0 }}
      />

      {/* Converging shields from completed steps */}
      <div className="relative z-10">
        {STEPS.slice(0, -1).map((step, i) => {
          // Shields start at spread positions, converge to center
          const startX = (i - 1) * 120;
          const startY = -20;

          return (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: '50%', top: '50%', marginLeft: -20, marginTop: -20 }}
              initial={{ x: startX, y: startY, scale: 1, opacity: 0.8 }}
              animate={{ x: 0, y: 0, scale: 0.8, opacity: 1 }}
              transition={{ duration: 0.4, delay: i * 0.08, ease: 'easeInOut' }}
            >
              <ShieldIcon size={40} filled glowColor="rgba(232, 148, 58, 0.4)" />
            </motion.div>
          );
        })}

        {/* Big ember burst at convergence point */}
        <motion.div
          className="absolute"
          style={{ left: '50%', top: '50%' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <EmberBurst count={20} intensity={2.5} />
        </motion.div>

        {/* Shatter fragments — 4 triangular pieces flying to corners */}
        {[
          { x: -200, y: -200, rotate: -45 },  // top-left
          { x: 200, y: -200, rotate: 45 },     // top-right
          { x: -200, y: 200, rotate: -135 },   // bottom-left
          { x: 200, y: 200, rotate: 135 },      // bottom-right
        ].map((target, i) => (
          <motion.div
            key={`shard-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              marginLeft: -12,
              marginTop: -12,
              width: 24,
              height: 24,
              clipPath: [
                'polygon(50% 0%, 0% 100%, 50% 70%)',
                'polygon(50% 0%, 100% 100%, 50% 70%)',
                'polygon(0% 0%, 50% 30%, 0% 100%)',
                'polygon(100% 0%, 50% 30%, 100% 100%)',
              ][i],
              backgroundColor: '#E8943A',
            }}
            initial={{ x: 0, y: 0, scale: 1, opacity: 0.9, rotate: 0 }}
            animate={{
              x: target.x,
              y: target.y,
              scale: 0.3,
              opacity: 0,
              rotate: target.rotate,
            }}
            transition={{ delay: 0.55, duration: 0.5, ease: 'easeOut' }}
          />
        ))}

        {/* Final flash */}
        <motion.div
          className="fixed inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.4, 0] }}
          transition={{ delay: 0.5, duration: 0.4, times: [0, 0.2, 1] }}
          onAnimationComplete={() => {
            setTimeout(onComplete, 300);
          }}
        >
          <div className="w-full h-full bg-[#E8943A]/20" />
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Resting Progress Tracker ───────────────────────────────────────────────

function ShieldTracker({
  currentStep,
  showGlow,
}: {
  currentStep: number;
  showGlow: boolean;
}) {
  return (
    <motion.div
      className="relative flex items-center gap-1 mb-10"
      animate={
        showGlow
          ? {
              filter: 'drop-shadow(0 4px 20px rgba(232, 148, 58, 0.1))',
            }
          : { filter: 'drop-shadow(0 0px 0px rgba(232, 148, 58, 0))' }
      }
      transition={{ duration: 0.6 }}
    >
      {STEPS.map((step, i) => {
        const isComplete = i < currentStep;
        const isCurrent = i === currentStep;
        return (
          <div key={step.label} className="flex items-center">
            <motion.div
              className="relative"
              animate={
                isCurrent
                  ? { scale: [1, 1.1, 1] }
                  : isComplete
                    ? { scale: 1 }
                    : { scale: 1 }
              }
              transition={
                isCurrent
                  ? { repeat: Infinity, duration: 2, ease: 'easeInOut' }
                  : {}
              }
            >
              <ShieldIcon
                size={28}
                filled={isComplete || isCurrent}
                glowColor={
                  isComplete
                    ? 'rgba(232, 148, 58, 0.3)'
                    : 'rgba(232, 148, 58, 0)'
                }
              />
              {/* Persistent ember glow on completed shields */}
              {isComplete && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  animate={{
                    boxShadow: [
                      '0 0 6px rgba(232, 148, 58, 0.15)',
                      '0 0 12px rgba(232, 148, 58, 0.25)',
                      '0 0 6px rgba(232, 148, 58, 0.15)',
                    ],
                  }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                />
              )}
              {/* Label */}
              <span
                className={`
                  absolute -bottom-5 left-1/2 -translate-x-1/2 text-[8px] font-mono tracking-wider uppercase whitespace-nowrap
                  ${isCurrent ? 'text-white/60' : isComplete ? 'text-[#E8943A]/50' : 'text-white/15'}
                `}
              >
                {step.label}
              </span>
            </motion.div>
            {i < STEPS.length - 1 && (
              <motion.div
                className="w-10 h-px mx-2"
                animate={{
                  backgroundColor: isComplete
                    ? 'rgba(232, 148, 58, 0.25)'
                    : 'rgba(255,255,255,0.04)',
                }}
                transition={{ duration: 0.6 }}
              />
            )}
          </div>
        );
      })}
    </motion.div>
  );
}

// ─── Prescription card (assembles from fragments) ───────────────────────────

function PrescriptionCard() {
  // Each section starts as a rotated shard at a random offset
  const sections = [
    { id: 'header', fromX: -80, fromY: -60, fromRotate: -8 },
    { id: 'risk', fromX: 100, fromY: -40, fromRotate: 12 },
    { id: 'rules', fromX: -60, fromY: 80, fromRotate: -6 },
    { id: 'footer', fromX: 90, fromY: 60, fromRotate: 10 },
  ];

  return (
    <div className="relative w-full max-w-[480px]">
      <div className="relative bg-white/[0.03] border border-[#E8943A]/15 rounded-xl p-8 backdrop-blur-md overflow-hidden">
        {/* Breathing glow */}
        <motion.div
          className="absolute -inset-2 rounded-xl pointer-events-none z-0"
          animate={{
            boxShadow: [
              '0 0 30px rgba(232, 148, 58, 0.06)',
              '0 0 50px rgba(232, 148, 58, 0.12)',
              '0 0 30px rgba(232, 148, 58, 0.06)',
            ],
          }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        />

        <div className="relative z-10 space-y-5">
          {/* Header — flies in from fragment position */}
          <motion.div
            initial={{ x: sections[0].fromX, y: sections[0].fromY, rotate: sections[0].fromRotate, opacity: 0, scale: 0.7 }}
            animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
            transition={{ type: 'spring' as const, stiffness: 200, damping: 20, delay: 0.1 }}
          >
            <p className="text-[10px] font-mono text-[#E8943A]/60 tracking-[0.2em] uppercase">
              Paladin Protocol
            </p>
          </motion.div>

          {/* Risk number — flies in from different position */}
          <motion.div
            initial={{ x: sections[1].fromX, y: sections[1].fromY, rotate: sections[1].fromRotate, opacity: 0, scale: 0.7 }}
            animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
            transition={{ type: 'spring' as const, stiffness: 200, damping: 20, delay: 0.2 }}
          >
            <div className="flex items-baseline gap-3">
              <span className="text-[11px] font-mono text-white/40 uppercase tracking-wider">
                Risk / Trade
              </span>
              <span className="text-3xl font-mono font-bold text-white">$187</span>
              <span className="text-lg font-mono text-white/50">1.5%</span>
            </div>
          </motion.div>

          {/* Rules — fly in as a group */}
          <motion.div
            initial={{ x: sections[2].fromX, y: sections[2].fromY, rotate: sections[2].fromRotate, opacity: 0, scale: 0.7 }}
            animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
            transition={{ type: 'spring' as const, stiffness: 200, damping: 20, delay: 0.3 }}
          >
            <div className="space-y-3">
              {['Max 2 contracts per entry', 'Hard stop at daily loss limit', 'No scaling into losers'].map(
                (rule, i) => (
                  <motion.div
                    key={rule}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-[#E8943A]/60" />
                    <span className="text-sm font-mono text-white/50">{rule}</span>
                  </motion.div>
                ),
              )}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ x: sections[3].fromX, y: sections[3].fromY, rotate: sections[3].fromRotate, opacity: 0, scale: 0.7 }}
            animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
            transition={{ type: 'spring' as const, stiffness: 200, damping: 20, delay: 0.4 }}
          >
            <div className="h-px bg-gradient-to-r from-transparent via-[#E8943A]/20 to-transparent" />
            <p className="text-[10px] font-mono text-white/20 mt-4 text-center">
              Generated Apr 14, 2026. Regenerate if stats change.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// ─── Step content card ──────────────────────────────────────────────────────

function StepCard({ label }: { label: string }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-6 py-5 backdrop-blur-md">
      <span className="text-sm font-mono text-white/50">{label}</span>
    </div>
  );
}

// ─── Main Preview Component ─────────────────────────────────────────────────

export function TransitionPreview3() {
  const [currentStep, setCurrentStep] = useState(0);
  const [forgeStep, setForgeStep] = useState<number | null>(null);
  const [showPrescriptionForge, setShowPrescriptionForge] = useState(false);
  const [contentReady, setContentReady] = useState(true);
  const [contentKey, setContentKey] = useState(0);

  const step = STEPS[currentStep];
  const isPrescription = !!step.isPrescription;

  const goToStep = useCallback(
    (next: number) => {
      if (next < 0 || next >= STEPS.length || next === currentStep || forgeStep !== null) return;

      const goingToPrescription = next === STEPS.length - 1;

      // Hide current content
      setContentReady(false);

      if (goingToPrescription) {
        // Special prescription forge
        setShowPrescriptionForge(true);
      } else {
        // Standard forge overlay
        setForgeStep(next);
      }
    },
    [currentStep, forgeStep],
  );

  const handleForgeComplete = useCallback(() => {
    const next = forgeStep!;
    setForgeStep(null);
    setCurrentStep(next);
    setContentKey((k) => k + 1);
    // Brief delay then show content
    setTimeout(() => setContentReady(true), 100);
  }, [forgeStep]);

  const handlePrescriptionForgeComplete = useCallback(() => {
    setShowPrescriptionForge(false);
    setCurrentStep(STEPS.length - 1);
    setContentKey((k) => k + 1);
    setTimeout(() => setContentReady(true), 100);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep <= 0 || forgeStep !== null) return;
    setContentReady(false);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setContentKey((k) => k + 1);
      setTimeout(() => setContentReady(true), 100);
    }, 200);
  }, [currentStep, forgeStep]);

  return (
    <div className="relative min-h-screen overflow-hidden select-none bg-[#060A12]">
      {/* Subtle background — no heavy layers, the forge overlay IS the show */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(15, 20, 35, 1) 0%, rgba(6, 10, 18, 1) 70%)',
        }}
      />

      {/* Forge overlay */}
      <AnimatePresence>
        {forgeStep !== null && (
          <ForgeOverlay stepIndex={forgeStep} onComplete={handleForgeComplete} />
        )}
      </AnimatePresence>

      {/* Prescription forge */}
      <AnimatePresence>
        {showPrescriptionForge && (
          <PrescriptionForge onComplete={handlePrescriptionForgeComplete} />
        )}
      </AnimatePresence>

      {/* ── Content layer ── */}
      <div className="relative z-10 min-h-screen flex flex-col items-center pt-14 px-8">
        {/* Shield tracker */}
        <ShieldTracker currentStep={currentStep} showGlow={isPrescription} />

        {/* Step label */}
        <AnimatePresence mode="wait">
          {contentReady && (
            <motion.div
              key={`label-${contentKey}`}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="text-center mt-4 mb-8"
            >
              <p className="text-[10px] font-mono text-[#E8943A]/50 tracking-[0.3em] uppercase mb-2">
                {step.label}
              </p>
              <h1 className="text-2xl font-mono font-bold text-white/90 tracking-wide">
                {step.title}
              </h1>
              <p className="text-sm font-mono text-white/30 mt-2">{step.subtitle}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step content */}
        <div className="relative w-full max-w-[520px] min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {contentReady && (
              isPrescription ? (
                <motion.div
                  key="prescription"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full flex justify-center"
                >
                  <PrescriptionCard />
                </motion.div>
              ) : (
                <motion.div
                  key={`step-${contentKey}`}
                  initial={{ opacity: 0, scale: 0.97, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, y: -10 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="w-full"
                >
                  <div className="grid gap-3">
                    {step.cards.map((card, i) => (
                      <motion.div
                        key={card}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.08 + i * 0.06, duration: 0.3 }}
                      >
                        <StepCard label={card} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )
            )}
          </AnimatePresence>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center gap-4 mt-12">
          <button
            onClick={handleBack}
            disabled={currentStep === 0 || forgeStep !== null}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === 0 || forgeStep !== null
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-white/[0.04] text-white/50 border border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'}
            `}
          >
            Back
          </button>

          <div className="text-[10px] font-mono text-white/20 tracking-wider">
            {currentStep + 1} / {STEPS.length}
          </div>

          <button
            onClick={() => goToStep(currentStep + 1)}
            disabled={currentStep === STEPS.length - 1 || forgeStep !== null}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === STEPS.length - 1 || forgeStep !== null
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-[#E8943A]/10 text-[#E8943A]/80 border border-[#E8943A]/20 hover:bg-[#E8943A]/20 hover:text-[#E8943A]'}
            `}
          >
            {currentStep === STEPS.length - 2 ? 'Forge Protocol' : 'Next'}
          </button>
        </div>

        {/* Concept label */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-full px-5 py-2 backdrop-blur-md">
            <span className="text-[10px] font-mono text-white/30 tracking-[0.2em] uppercase">
              Concept 3 — The Shield Tempering
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
